import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateQueriesFor, useApiConfig } from "@visioni/a3s-client-api/app";
import type { ProductInterest, UpsellStatus } from "../schema";
import { submitUpsellRequestMutation, upsellStatusResource } from "./queries";

export interface UseSubmitUpsellRequestOptions {
  readonly conversionPage: string;
  readonly siteId?: string;
  readonly onSuccess?: (productInterest: ProductInterest) => void;
}

export interface UseSubmitUpsellRequestResult {
  readonly submit: () => Promise<UpsellStatus>;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
}

export const useSubmitUpsellRequest = (
  productInterest: ProductInterest,
  options: UseSubmitUpsellRequestOptions,
): UseSubmitUpsellRequestResult => {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const mutationConfig = useMemo(
    () =>
      apiConfig.tenant ?
        {
          tenantId: apiConfig.tenant.tenantId,
          apiBaseUrl: apiConfig.tenant.apiBaseUrl,
          authenticatedFetch: apiConfig.authenticatedFetch,
        }
      : { authenticatedFetch: apiConfig.authenticatedFetch },
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const mutationOptions = useMemo(() => submitUpsellRequestMutation(mutationConfig, {}), [mutationConfig]);

  const mutation = useMutation({
    mutationFn: () =>
      mutationOptions.mutationFn({
        productInterest,
        conversionPage: options.conversionPage,
        ...(options.siteId !== undefined ? { siteId: options.siteId } : {}),
      }),
    onSuccess: () => {
      options.onSuccess?.(productInterest);
      const tenantId = apiConfig.tenant?.tenantId;
      void queryClient.invalidateQueries(
        invalidateQueriesFor(upsellStatusResource, tenantId ? { path: { tenantId } } : undefined),
      );
    },
  });

  return {
    submit: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error instanceof Error ? mutation.error : null,
  };
};
